import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { apiGet } from '../../lib/api';
import { Card } from '../../components/Card';
import { Money } from '../../components/Money';
import { Button } from '../../components/Button';
import { EmptyState } from '../../components/EmptyState';
import { PageScaffold } from '../../components/PageScaffold';
import { DataTableCard, STICKY_HEAD } from '../../components/DataTable';
import { cn } from '../../lib/cn';

interface Receivables {
  rows: Array<{
    customer: {
      id: string;
      type: string;
      name: string;
      companyName: string | null;
      phone: string;
      email: string | null;
    };
    buckets: Record<'0-30' | '31-60' | '61-90' | '90+', number>;
    totalDue: number;
    orders: Array<{
      id: string;
      number: string;
      balanceDue: string;
      daysOverdue: number;
      shop: string;
    }>;
  }>;
  totals: { '0-30': number; '31-60': number; '61-90': number; '90+': number; total: number };
}

type BucketKey = '0-30' | '31-60' | '61-90' | '90+';

export function ReceivablesPage(): JSX.Element {
  const ar = useQuery({
    queryKey: ['receivables'],
    queryFn: () => apiGet<Receivables>('/finance/receivables'),
  });
  const [filter, setFilter] = useState<'all' | BucketKey>('all');

  return (
    <PageScaffold
      scroll={false}
      title="Accounts receivable"
      subtitle="Outstanding customer balances bucketed by age."
      actions={
        <Button variant="ghost" disabled title="Phase 2">
          Send batch reminders
        </Button>
      }
    >
      {ar.isLoading || !ar.data ? (
        <div className="text-center text-ink-3 py-12">Loading…</div>
      ) : ar.data.rows.length === 0 ? (
        <Card>
          <EmptyState title="No outstanding balances" description="Every customer is paid up." />
        </Card>
      ) : (
        <>
          {/* Aging tiles — clickable filter */}
          <div className="shrink-0 grid grid-cols-2 md:grid-cols-5 gap-3">
            <Bucket
              label="0–30 days"
              value={ar.data.totals['0-30']}
              count={ar.data.rows.filter((r) => r.buckets['0-30'] > 0).length}
              tone="positive"
              active={filter === '0-30'}
              onClick={() => setFilter(filter === '0-30' ? 'all' : '0-30')}
            />
            <Bucket
              label="31–60"
              value={ar.data.totals['31-60']}
              count={ar.data.rows.filter((r) => r.buckets['31-60'] > 0).length}
              tone="warning"
              active={filter === '31-60'}
              onClick={() => setFilter(filter === '31-60' ? 'all' : '31-60')}
            />
            <Bucket
              label="61–90"
              value={ar.data.totals['61-90']}
              count={ar.data.rows.filter((r) => r.buckets['61-90'] > 0).length}
              tone="warning"
              active={filter === '61-90'}
              onClick={() => setFilter(filter === '61-90' ? 'all' : '61-90')}
            />
            <Bucket
              label="90+"
              value={ar.data.totals['90+']}
              count={ar.data.rows.filter((r) => r.buckets['90+'] > 0).length}
              tone="negative"
              active={filter === '90+'}
              onClick={() => setFilter(filter === '90+' ? 'all' : '90+')}
            />
            <Bucket
              label="Total outstanding"
              value={ar.data.totals.total}
              count={ar.data.rows.length}
              tone="accent"
              active={filter === 'all'}
              onClick={() => setFilter('all')}
            />
          </div>

          <DataTableCard>
            <table className="w-full text-sm">
              <thead className={STICKY_HEAD}>
                <tr className="text-left text-xs uppercase tracking-wide text-ink-3 border-b border-rule">
                  <th className="px-6 py-2 font-medium">Customer</th>
                  <th className="px-3 py-2 font-medium text-right">0–30</th>
                  <th className="px-3 py-2 font-medium text-right">31–60</th>
                  <th className="px-3 py-2 font-medium text-right">61–90</th>
                  <th className="px-3 py-2 font-medium text-right">90+</th>
                  <th className="px-3 py-2 font-medium text-right">Total due</th>
                  <th className="px-3 py-2 font-medium text-right">Oldest</th>
                  <th className="px-6 py-2 font-medium">Remind</th>
                </tr>
              </thead>
              <tbody>
                {ar.data.rows
                  .filter((r) => filter === 'all' || r.buckets[filter] > 0)
                  .map((r) => {
                    const oldest = r.orders.reduce((max, o) => Math.max(max, o.daysOverdue), 0);
                    return (
                      <tr key={r.customer.id} className="border-b border-rule-2 hover:bg-paper-2">
                        <td className="px-6 py-3">
                          <Link
                            to={`/customers/${r.customer.id}`}
                            className="text-ink hover:text-accent font-medium"
                          >
                            {r.customer.type === 'company'
                              ? (r.customer.companyName ?? r.customer.name)
                              : r.customer.name}
                          </Link>
                          {r.customer.type === 'company' && (
                            <div className="text-xs text-ink-3">Contact: {r.customer.name}</div>
                          )}
                          <div className="text-xs text-ink-3 kc-mono">
                            {r.customer.phone}
                            {r.customer.email ? ` · ${r.customer.email}` : ''}
                          </div>
                        </td>
                        <td className="px-3 py-3 text-right">
                          <Money value={r.buckets['0-30']} muted={r.buckets['0-30'] === 0} />
                        </td>
                        <td className="px-3 py-3 text-right">
                          <Money
                            value={r.buckets['31-60']}
                            muted={r.buckets['31-60'] === 0}
                            className={r.buckets['31-60'] > 0 ? 'text-warning' : ''}
                          />
                        </td>
                        <td className="px-3 py-3 text-right">
                          <Money
                            value={r.buckets['61-90']}
                            muted={r.buckets['61-90'] === 0}
                            className={r.buckets['61-90'] > 0 ? 'text-warning' : ''}
                          />
                        </td>
                        <td className="px-3 py-3 text-right">
                          <Money
                            value={r.buckets['90+']}
                            muted={r.buckets['90+'] === 0}
                            className={r.buckets['90+'] > 0 ? 'text-negative' : ''}
                          />
                        </td>
                        <td className="px-3 py-3 text-right font-medium">
                          <Money value={r.totalDue} className="text-accent" />
                        </td>
                        <td className="px-3 py-3 text-right">
                          <span
                            className={cn(
                              'text-xs kc-mono',
                              oldest > 90
                                ? 'text-negative'
                                : oldest > 60
                                  ? 'text-warning'
                                  : 'text-ink-3',
                            )}
                          >
                            {oldest}d
                          </span>
                        </td>
                        <td className="px-6 py-3">
                          <div className="flex gap-1">
                            <Button
                              size="sm"
                              variant="ghost"
                              disabled
                              title="Phase 2 — WhatsApp Business API"
                            >
                              💬 WhatsApp
                            </Button>
                            <Button size="sm" variant="ghost" disabled title="Phase 2 — Email">
                              ✉ Email
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </DataTableCard>
        </>
      )}
    </PageScaffold>
  );
}

function Bucket({
  label,
  value,
  count,
  tone,
  active,
  onClick,
}: {
  label: string;
  value: number;
  count: number;
  tone: 'positive' | 'warning' | 'negative' | 'accent';
  active: boolean;
  onClick: () => void;
}): JSX.Element {
  const baseColors: Record<typeof tone, string> = {
    positive: 'border-positive/30 bg-positive-soft text-positive',
    warning: 'border-warning/30 bg-warning-soft text-warning',
    negative: 'border-negative/30 bg-negative-soft text-negative',
    accent: 'border-accent/30 bg-accent-soft text-accent',
  };
  return (
    <button
      onClick={onClick}
      className={cn(
        'rounded-lg border p-4 text-left transition',
        baseColors[tone],
        active
          ? 'ring-2 ring-current ring-offset-2 ring-offset-paper'
          : 'opacity-90 hover:opacity-100',
      )}
    >
      <div className="text-xs uppercase tracking-wide opacity-70">{label}</div>
      <div className="text-xl font-medium mt-1 kc-mono kc-tnum">
        <Money value={value} className="text-current" />
      </div>
      <div className="text-[10px] opacity-70 mt-1">
        {count} customer{count === 1 ? '' : 's'}
      </div>
    </button>
  );
}
